/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { clearLogs, fetchLogsData } from '@actions/logs-actions';
import {
  dotmapsBlack40Alt,
  dotmapsBlack80,
  dotmapsVeryLightGray,
  overlayTextColor
} from '@constants/colors';
import LogItem from '@shared/comments/log-item';
import CustomChip from '@shared/custom-chip';
import DotmapsLoader from '@shared/dotmaps-loader';
import CommentBox from '@shared/dialogs/overlap-status-dialog/actions-comment-box';
import { allowResolution } from '@utils/permission-utils';
import './logs-box.scss';

const LogsBox = ({
  app,
  id,
  isOpportunity,
  mainId,
  mainTypeName,
  model,
  typeName
}) => {
  const dispatch = useDispatch();

  // Redux state
  const items = useSelector(state => state.logs.list);
  const loading = useSelector(state => state.logs.loading);
  const next = useSelector(state => state.logs.next);
  const reload = useSelector(state => state.logs.reload);

  const [filter, setFilter] = useState('All');

  const initialLoad = () => {
    // Logs must be cleared before the first retrieval, since they were
    // designed to always append (when we scroll), it doesn't work like
    // all other fetch APIs in this app.
    dispatch(clearLogs());
    dispatch(fetchLogsData(app, model, mainId, null, { filters: 'comment_filters' }));
  };

  // Initial load on mount
  useEffect(() => {
    initialLoad();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload when reload prop changes to true
  useEffect(() => {
    if (reload) {
      initialLoad();
    }
  }, [reload]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return all log items, including only the ones related to the two entities
  // of this overlap.
  //
  // The Activity Log API call returns all entries about 'resolve', 'revoke' and 'comment'
  // actions for the specified entity ID, however for 'resolve' and 'revoke', all records
  // where this entity ID is referenced are retrieved and that includes other entity
  // IDs other than then second entity ID of this overlap.
  //
  // We need to restrict the list to only 'resolve' and 'revoke' actions that belong
  // only to the two entity IDs of this overlap.
  const getItems = () => {
    return items.filter(item => {
      const action = item?.entry?.action;
      const id1 = item?.attrs?.id1;
      const id2 = item?.attrs?.id2;
      // 'resolve' or 'revoke' actions contain id1 and  id2 attributes.
      if ((action === 'overlap_resolve' || action === 'overlap_revoke') && id1 && id2) {
        // For resolve/revoke, include the item only if the two ids in this overlap
        // are both referenced in the log entry attributes.
        return (id1 === id && id2 === mainId) || (id1 === mainId && id2 === id);
      }
      // For comments, check if one of the ids matches the overlapping entity.
      if (action === 'overlap_comment' && id1 && id2) {
        return id === id1 || id === id2;
      }
      return true;
    });
  };

  // Return items filtered by the currently selected filter.
  const getFilteredItems = () => {
    const allItems = getItems();
    if (filter === 'Comments') {
      return allItems.filter(item => item?.entry?.action === 'overlap_comment');
    } else if (filter === 'Resolutions') {
      return allItems.filter(
        item => item?.entry?.action === 'overlap_resolve' || item?.entry?.action === 'overlap_revoke'
      );
    }
    // Else, filter === 'All':
    return allItems;
  };

  // Returns true if there any log item about a resolve or a revoke:
  const hasResolutionItems = () => getItems().filter(
    item => item?.entry?.action === 'overlap_resolve' || item?.entry?.action === 'overlap_revoke'
  ).length > 0;

  // Return the index of the first specified action.
  const getIndex = action => getItems().findIndex(item => item?.entry?.action === action);

  const renderLogs = () => getFilteredItems().map(
    (item, index) => (
      <LogItem
        key={index}
        index={index}
        item={item}
        resolveIndex={getIndex('overlap_resolve')}
        revokeIndex={getIndex('overlap_revoke')}
      />
    ));

  const renderFilterChip = chipId => (
    <div styleName="filter">
      <CustomChip
        colors={{
          background: filter === chipId ? dotmapsBlack40Alt : dotmapsVeryLightGray,
          foreground: filter === chipId ? overlayTextColor : dotmapsBlack80
        }}
        label={chipId}
        onClick={() => setFilter(chipId)}
      />
    </div>
  );

  const renderFilters = () => {
    // Hide filters if this entity or the main entity don't
    // allow resolution:
    if (!allowResolution(mainTypeName, typeName)) {
      return null;
    }
    // Also hide it, if it's an opportunity and there are
    // no resolve/revoke items (which means it wasn't
    // previously a conflict).
    if (isOpportunity && !hasResolutionItems()) {
      return null;
    }
    return (
      <div styleName="button-filters">
        <div styleName="filter">Show:</div>
        {renderFilterChip('All')}
        {renderFilterChip('Comments')}
        {renderFilterChip('Resolutions')}
      </div>
    );
  };

  const filteredItems = getFilteredItems();
  const hasLogs = filteredItems && filteredItems?.length > 0;

  return (
    <div styleName="logs-box-container">
      <div styleName="title">
        Activity
      </div>
      {renderFilters()}
      <CommentBox />
      <div styleName="log-list">
        {hasLogs && renderLogs()}
        {!hasLogs && !loading && (
          <span styleName="empty">No activity to show.</span>
        )}
      </div>
      <DotmapsLoader display={loading && !next} />
      {/* When we load more through scrolling, show the indicator at the bottom. */}
      {loading && next && (
        <div styleName="spinner">
          <DotmapsLoader display={loading && next} />
        </div>
      )}
    </div>
  );
};

LogsBox.propTypes = {
  app: PropTypes.string,
  id: PropTypes.number,
  isOpportunity: PropTypes.bool,
  mainId: PropTypes.number,
  mainTypeName: PropTypes.string,
  model: PropTypes.string,
  typeName: PropTypes.string
};

export default LogsBox;
